// Static version tracker for the SPY RSI Strategy (backend/cmd/spyrsibacktest).
// Manually maintained - updated automatically in the same turn a strategy
// rule changes and is re-backtested. Not fetched from anywhere; this file
// IS the record.
//
// How to extend:
//   - Tweaking a version in place (e.g. re-enabling a condition) - just
//     edit that version's `has` set.
//   - A brand-new condition - insert it as a new row NEAR its related
//     condition (not necessarily at the bottom - group by topic for
//     readability), set its `addedAt` to the date it was introduced, and
//     add its index to `has` for every version that includes it (versions
//     that predate it, or that omit it, render a red X automatically since
//     they don't list that index).
//   - One condition REPLACING another - add the new condition as its own
//     row (near the old one), add its index to the new version's `has`,
//     and drop the old condition's index from that version's `has` (the
//     old row stays in the table, now showing X for that version onward,
//     and keeps its checks for every earlier version).
const CONDITIONS = [
  { text: 'RSI(7) on the SPY 1-min chart drives all signals (never the option chain itself)', addedAt: '2026-07-13' },
  { text: 'RSI SMA(7) is defined but not used by any rule yet', addedAt: '2026-07-13' },
  { text: 'Entry: RSI crosses up through 30 → buy the nearest listed call strike above spot', addedAt: '2026-07-13' },
  { text: 'Contract used is the next trading day’s expiry (holiday-aware, skips weekends/holidays)', addedAt: '2026-07-13' },
  { text: 'No new entries after 2:30 PM ET; forced exit by 3:59:30 PM ET if nothing else fired', addedAt: '2026-07-13' },
  { text: 'One open position at a time; multiple trades allowed per day (not concurrent)', addedAt: '2026-07-13' },
  { text: 'Exit (TP): RSI crosses up through or touches 68', addedAt: '2026-07-13' },
  { text: 'Exit (SL): RSI crosses down through 45', addedAt: '2026-07-13' },
  { text: 'Fixed stop-loss at -70% from entry', addedAt: '2026-07-13' },
  { text: 'Fixed stop-loss at -30% from entry', addedAt: '2026-07-13' },
  { text: 'Stop-loss ratchets (once) to -15% from entry after unrealized gain touches +30%', addedAt: '2026-07-13' },
  { text: 'Starting budget $500 as one running total (not a per-trade cap); an unaffordable signal is skipped, not the whole day', addedAt: '2026-07-13' },
  { text: '$7/contract flat round-trip cost', addedAt: '2026-07-13' },
];

const VERSIONS = [
  {
    name: 'Original Strategy',
    note: '2026-07-13 — first draft as specified',
    has: new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, /* 9 (-30% SL) not yet introduced */ 10, 11, 12]),
  },
  {
    name: 'Version 1',
    note: '2026-07-13 — RSI SL (cross down 45) disabled, everything else unchanged',
    has: new Set([0, 1, 2, 3, 4, 5, 6, /* 7 removed */ 8, 10, 11, 12]),
  },
  {
    name: 'Version 2',
    note: '2026-07-13 — fixed stop-loss changed -70% → -30% (RSI SL still disabled)',
    has: new Set([0, 1, 2, 3, 4, 5, 6, /* 7 removed */ /* 8 replaced by 9 */ 9, 10, 11, 12]),
  },
];

export default function StrategyVersionTracker() {
  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          SPY RSI Strategy — Version Tracker
        </span>
      </div>
      <div className="text-xs text-gray-500 mb-3">
        Manually maintained record of which rules each backtest version ran with. Green check = rule active in that version, red X = not active.
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              <th className="py-2 pr-3 text-left font-medium" style={{ width: 36 }}>#</th>
              <th className="py-2 pr-3 text-left font-medium">Condition</th>
              {VERSIONS.map((v) => (
                <th key={v.name} className="py-2 px-3 text-center font-medium" style={{ minWidth: 120 }}>
                  <div className="text-gray-300">{v.name}</div>
                  <div className="text-[10px] font-normal text-gray-600 normal-case">{v.note}</div>
                </th>
              ))}
              <th className="py-2 pl-3 text-right font-medium" style={{ minWidth: 90 }}>Added</th>
            </tr>
          </thead>
          <tbody>
            {CONDITIONS.map((cond, i) => (
              <tr key={i} className="border-b" style={{ borderColor: '#1f2937' }}>
                <td className="py-2 pr-3 text-gray-500 font-mono">{i + 1}</td>
                <td className="py-2 pr-3 text-gray-300">{cond.text}</td>
                {VERSIONS.map((v) => (
                  <td key={v.name} className="py-2 px-3 text-center font-bold" style={{ color: v.has.has(i) ? '#22c55e' : '#ef4444' }}>
                    {v.has.has(i) ? '✓' : '✗'}
                  </td>
                ))}
                <td className="py-2 pl-3 text-right text-gray-500 font-mono">{cond.addedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
